import React, { useMemo } from 'react';
import { DiscoverFilter, PromoVisualOptions } from '@box-types';
import { ImageTile } from '@box/component-library';
import {
  couponSpecificPromoOptions,
  generateImageSrc,
  getDiscoverFilterMultiplierTag,
  getSpecificPromoCampaignCoupons
} from '@box/utils';
import { useInjector } from '@box-react/hooks/use-injector';
import { GlobalStateService } from '@box-core/services/global-state.service';
import { PromoCampaignsService } from '@box-core/services';
import { PromoTagComponent } from '../promo-tag';

type HomeQuickFilterProps = {
  filter: DiscoverFilter;
  onToggle?: (filter: DiscoverFilter) => void;
};

const HomeQuickFilter: React.FC<HomeQuickFilterProps> = ({ filter, onToggle }) => {
  const promoCampaignsService = useInjector().get(PromoCampaignsService);
  const globalStateService = useInjector().get(GlobalStateService);

  const getDiscoverFilterCouponSpecificVisualOptions = (filter: DiscoverFilter): PromoVisualOptions[] => {
    const filterEligibleCouponSynergies = filter?.eligibleCouponSynergies;
    if (!filterEligibleCouponSynergies?.length) return;

    const availableCoupons = globalStateService.getAvailableCoupons();
    const promoCampaigns = promoCampaignsService.getActivePromoCampaigns();

    const couponsPromos = getSpecificPromoCampaignCoupons(availableCoupons, promoCampaigns);
    if (!couponsPromos?.length) return;

    const couponsFilterPromo = couponsPromos.filter((coupon) => filterEligibleCouponSynergies.includes(coupon.synergy));

    const couponVisualOptions = couponSpecificPromoOptions(couponsFilterPromo, 'shortTag');
    return [couponVisualOptions];
  };

  const tags = useMemo(
    () => getDiscoverFilterCouponSpecificVisualOptions(filter) ?? getDiscoverFilterMultiplierTag(filter),
    [filter]
  );

  const imageUrl = filter.mainViewWebImage
    ? useMemo(() => generateImageSrc(filter.mainViewWebImage), [filter])
    : useMemo(() => generateImageSrc(filter.webImage), [filter]);

  const altText = filter.title;

  const handleClick = () => {
    onToggle?.(filter);
  };

  return (
    <div
      role="link"
      tabIndex={0}
      className="home-quick-filter-component hover-opacity hoverable-animated"
      onClick={handleClick}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      {tags?.length > 0 && (
        <div className="home-quick-filter-component-tags">
          {tags.map((tag, index) => (
            <PromoTagComponent key={index} options={tag} enableAnimations={true} className="box-promo-tag" />
          ))}
        </div>
      )}

      <ImageTile imgUrl={imageUrl} altText={altText} />
    </div>
  );
};

export default React.memo(HomeQuickFilter);
